import { motion } from "framer-motion";
import { TrendingUp, DollarSign, TrendingDown, AlertTriangle, Percent, Zap } from "lucide-react";
import { LogoMark } from "./Logo";

const ORBIT = ["Revenue", "Sales", "Marketing", "Customers", "CRM", "Pipeline", "Advertising", "Margin", "Vendors", "Operating Costs", "Automation"];

const DETECTIONS = [
    { icon: TrendingUp, label: "Revenue Opportunity", tone: "text-vantera border-vantera/40" },
    { icon: DollarSign, label: "Dormant Revenue", tone: "text-azure border-azure/40" },
    { icon: TrendingDown, label: "Vendor Cost", tone: "text-vantera border-vantera/40" },
    { icon: AlertTriangle, label: "Margin Leakage", tone: "text-amber-400 border-amber-400/40" },
    { icon: Percent, label: "Conversion Opportunity", tone: "text-azure border-azure/40" },
    { icon: Zap, label: "Automation Opportunity", tone: "text-vantera border-vantera/40" },
];

const XRayScanner = () => (
    <div data-testid="xray-scanner">
        <div className="relative mx-auto aspect-square w-full max-w-[540px]" aria-label="Business X-Ray scan of a company across eleven operational categories">
            <div className="absolute inset-0 rounded-full border border-white/10" aria-hidden="true" />
            <div className="absolute inset-[11%] rounded-full border border-white/[0.07]" aria-hidden="true" />
            <div className="absolute inset-[23%] rounded-full border border-dashed border-white/[0.09]" aria-hidden="true" />
            <div className="absolute inset-0 rounded-full animate-spin-slow" style={{ background: "conic-gradient(from 0deg, rgba(30,136,247,0.3), rgba(30,136,247,0.04) 50deg, transparent 72deg)" }} aria-hidden="true" />
            <div className="absolute left-0 right-0 top-1/2 h-px bg-white/[0.06]" aria-hidden="true" />
            <div className="absolute bottom-0 top-0 left-1/2 w-px bg-white/[0.06]" aria-hidden="true" />

            <div className="absolute inset-[31%] z-10 flex flex-col items-center justify-center gap-2 rounded-xl border border-vantera/40 bg-ink2 scan-sweep p-4 text-center">
                <LogoMark size={26} />
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ivory">The Company</p>
                <p className="font-mono text-[8.5px] uppercase tracking-[0.16em] text-cloud">Revenue engine · Operating economics</p>
            </div>

            {ORBIT.map((c, i) => {
                const a = (i / ORBIT.length) * Math.PI * 2 - Math.PI / 2;
                const x = 50 + 47 * Math.cos(a);
                const y = 50 + 47 * Math.sin(a);
                return (
                    <motion.div
                        key={c}
                        initial={{ opacity: 0, scale: 0.7 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.3 + i * 0.09 }}
                        className="absolute z-10 hidden -translate-x-1/2 -translate-y-1/2 md:block"
                        style={{ left: `${x}%`, top: `${y}%` }}
                    >
                        <span className="whitespace-nowrap rounded-full border border-white/15 bg-ink/90 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-ivory/85 backdrop-blur">
                            {c}
                        </span>
                    </motion.div>
                );
            })}
        </div>

        <div className="mx-auto mt-4 flex max-w-[540px] flex-wrap justify-center gap-2 md:hidden" aria-label="Scanned categories">
            {ORBIT.map((c, i) => (
                <motion.span key={c} initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }} className="rounded-full border border-white/15 bg-ink/90 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-ivory/85">
                    {c}
                </motion.span>
            ))}
        </div>

        <div className="mx-auto mt-10 grid max-w-[820px] gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="xray-detections">
            {DETECTIONS.map((d, i) => (
                <motion.div
                    key={d.label}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.55, delay: 0.4 + i * 0.14 }}
                    className={`flex items-center gap-3 rounded-lg border bg-white/[0.03] px-4 py-3.5 ${d.tone}`}
                >
                    <d.icon size={16} strokeWidth={1.75} aria-hidden="true" />
                    <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-ivory/90">{d.label}</span>
                </motion.div>
            ))}
        </div>
    </div>
);

export default XRayScanner;